/**
 * 1. The function will only execute every waitMs milliseconds.
 * 2. If the function is called again before waitMs milliseconds, that call will be
 *    saved. If the function isn't called again in waitMs milliseconds, that call of
 *    the function will be executed ("trailing").
 * 3. If the function is called multiple times in waitMs milliseconds, only the last
 *    call will be used.
 */
export function debounceTrailing(func, waitMs, 
/** If provided, when the signal is aborted, no further calls will be made to
 * `func`. */
abortSignal) {
    let timeoutId = null;
    // the last time func was executed (not necessarily the last time it was
    // called)
    let lastExecutionMs = 0;
    // the arguments to the last call of the function (not the last execution) are
    // stored here
    let lastArgs = null;
    // we want to clear the timeout because Node will wait for the timeout to run
    // before exiting, even though the timeout won't actually do anything after
    // aborting
    abortSignal?.addEventListener("abort", () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    });
    // a wrapper so that we can do some things every time the function is called
    const funcWrapper = (...args) => {
        if (abortSignal?.aborted) {
            return;
        }
        lastExecutionMs = Date.now();
        func(...args);
    };
    return (...args) => {
        lastArgs = args;
        if (abortSignal?.aborted) {
            return;
        }
        if (timeoutId === null) {
            const now = Date.now();
            const msSinceLastCall = now - lastExecutionMs;
            // if the function hasn't been called recently enough, call it immediately
            if (msSinceLastCall >= waitMs) {
                funcWrapper(...args);
            }
            else {
                timeoutId = setTimeout(() => {
                    if (lastArgs) {
                        funcWrapper(...lastArgs);
                    }
                    timeoutId = null;
                    // wait the remaining time since the last call
                }, waitMs - msSinceLastCall);
            }
        }
    };
}
